import { Box, Button, FormHelperText } from "@mui/material";
import React from "react";

interface Props {
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  rows?: number;
}

/**
 * Lightweight JSON "code" editor: a monospace textarea where Tab inserts an
 * indent (instead of moving focus) and a Format button pretty-prints valid
 * JSON. No external editor dependency. Validation/persistence is the caller's.
 */
export default function JsonCodeField({
  value,
  onChange,
  error,
  rows = 10,
}: Props) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const ta = e.currentTarget;
    const { selectionStart: start, selectionEnd: end } = ta;
    onChange(value.slice(0, start) + "  " + value.slice(end));
    // Restore the caret just past the inserted indent after React re-renders.
    requestAnimationFrame(() => {
      ta.selectionStart = ta.selectionEnd = start + 2;
    });
  };

  const format = () => {
    try {
      onChange(JSON.stringify(JSON.parse(value || "{}"), null, 2));
    } catch {
      // Invalid JSON — leave as-is; the caller surfaces the error.
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
        <Button size="small" onClick={format}>
          Format
        </Button>
      </Box>
      <Box
        component="textarea"
        value={value}
        spellCheck={false}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        onKeyDown={handleKeyDown}
        rows={rows}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          fontFamily: "monospace",
          fontSize: "0.85rem",
          lineHeight: 1.5,
          tabSize: 2,
          p: 1.5,
          borderRadius: 1,
          border: "1px solid",
          borderColor: error ? "error.main" : "divider",
          bgcolor: "background.default",
          color: "text.primary",
          resize: "vertical",
          outline: "none",
          "&:focus": { borderColor: "primary.main" },
        }}
      />
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
    </Box>
  );
}
